import { Link, usePage } from '@inertiajs/react'
import { useState } from 'react'
import Button from '../ui/Button'
import MobileMenu from './MobileMenu'

/**
 * Header component with navigation
 * Responsive header with desktop and mobile navigation
 *
 * @component
 */
export default function Header() {
  const page = usePage()
  const url = page.url
  const auth = page.props.auth
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)

  // Debug: afficher les props auth dans la console
  console.log('Header - auth props:', auth)

  const isActive = (path) => {
    if (path === '/') {
      return url === '/'
    }
    return url.startsWith(path)
  }

  // Public navigation links
  const publicLinks = [
    { href: '/', label: 'Accueil' },
    { href: '/agences', label: 'Agences' },
    { href: '/tarifs', label: 'Tarifs' },
    { href: '/contact', label: 'Contact' },
  ]

  // Authenticated agency links (gestionnaire)
  const agencyLinks = [
    { href: '/dashboard', label: 'Dashboard' },
    { href: '/leads', label: 'Leads' },
    { href: '/properties', label: 'Propriétés' },
    { href: '/matches', label: 'Matches' },
    { href: '/credits', label: 'Crédits' },
    { href: '/transactions', label: 'Transactions' },
  ]

  // Authenticated admin links
  const adminLinks = [
    { href: '/admin', label: 'Dashboard' },
    { href: '/leads', label: 'Leads' },
    { href: '/properties', label: 'Propriétés' },
    { href: '/matches', label: 'Matches' },
    { href: '/credits', label: 'Crédits' },
    { href: '/transactions', label: 'Transactions' },
    { href: '/collaboration/settings', label: 'Collaboration' },
    { href: '/admin/agences', label: 'Agences' },
  ]

  // Determine which links to show based on auth status
  // When authenticated, show only public links in header (agency/admin links are in their respective layouts)
  const navigationLinks = auth?.user ? publicLinks : publicLinks

  return (
    <header className="sticky top-0 z-50 bg-white border-b border-gray-200 shadow-sm">
      <nav className="container mx-auto px-4" aria-label="Navigation principale">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <Link href="/" className="flex items-center gap-2 hover:opacity-80 transition-opacity">
            <div className="h-8 w-8 bg-primary-600 rounded-lg flex items-center justify-center">
              <span className="text-white font-bold text-lg">S</span>
            </div>
            <span className="text-xl font-bold text-gray-900">SmartImmo</span>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-6">
            {navigationLinks.map((link) => (
              <Link
                key={link.href}
                href={link.href}
                className={`text-sm font-medium transition-colors relative ${
                  isActive(link.href)
                    ? 'text-primary-600'
                    : 'text-gray-600 hover:text-primary-600'
                }`}
              >
                {link.label}
                {isActive(link.href) && (
                  <span className="absolute -bottom-[21px] left-0 right-0 h-0.5 bg-primary-600" />
                )}
              </Link>
            ))}
          </div>

          {/* Desktop Actions */}
          <div className="hidden md:flex items-center gap-3">
            {auth?.user ? (
              <>
                <Link href={auth.user.admin ? '/admin' : '/dashboard'}>
                  <Button variant="primary" size="sm">
                    {auth.user.admin ? 'Admin' : 'Dashboard'}
                  </Button>
                </Link>
                <Link href="/sign-out" method="delete" as="button">
                  <Button variant="outline" size="sm">
                    Déconnexion
                  </Button>
                </Link>
              </>
            ) : (
              <>
                <Link href="/sign-in">
                  <Button variant="ghost" size="sm">
                    Connexion
                  </Button>
                </Link>
                <Link href="/sign-up">
                  <Button variant="primary" size="sm">
                    Créer un compte
                  </Button>
                </Link>
              </>
            )}
          </div>

          {/* Mobile Menu Button */}
          <button
            className="md:hidden p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
            onClick={() => setMobileMenuOpen(true)}
            aria-label="Ouvrir le menu"
            aria-expanded={mobileMenuOpen}
          >
            <svg
              className="w-6 h-6"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              aria-hidden="true"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M4 6h16M4 12h16M4 18h16"
              />
            </svg>
          </button>
        </div>
      </nav>

      {/* Mobile Menu */}
      <MobileMenu
        isOpen={mobileMenuOpen}
        onClose={() => setMobileMenuOpen(false)}
        links={navigationLinks}
        auth={auth}
        isActive={isActive}
      />
    </header>
  )
}
